import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from "@nexus/ui";
import { ArrowLeft, Plus } from "lucide-react";
import type {
  AdminVendorRow,
  PendingVendorOnboardingContext,
  PendingVendorOnboardingRow,
} from "@/features/admin/marketplace/types/admin-marketplace.types";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { adminPendingHandoffsQueryOptions, adminPendingVendorsQueryOptions } from "@/lib/queries";
import { AdminVendorsList } from "../components/AdminVendorsList";
import { AdminPendingVendorsList } from "../components/AdminPendingVendorsList";
import { AdminPendingHandoffsList } from "../components/AdminPendingHandoffsList";
import { AdminVendorForm } from "../components/AdminVendorForm";
import { AdminTeamRecipients } from "../components/AdminTeamRecipients";
import { AdminSlackNotifications } from "../components/AdminSlackNotifications";
import { useMarketplaceAdminCapability } from "../hooks/useMarketplaceAdminCapability";

type AdminTab = "vendors" | "pending" | "handoffs" | "team-recipients" | "slack-notifications";

type PageMode =
  | { kind: "list"; tab: AdminTab }
  | {
      kind: "form";
      editingVendor?: AdminVendorRow;
      pendingContext?: PendingVendorOnboardingContext;
    };

function toPendingContext(row: PendingVendorOnboardingRow): PendingVendorOnboardingContext {
  return {
    id: row.id,
    rawLabel: row.rawLabel,
    tenantId: row.tenantId,
    tenantName: row.tenant?.name ?? row.tenantId,
    contactEmail: row.contactEmail,
  };
}

export function MarketplaceAdminPage() {
  const { t } = useTranslation();
  const perms = usePermissions();
  const canMarketplaceAdmin = useMarketplaceAdminCapability();
  // API grants these via marketplace.admin page-bundle inheritance; the JWT
  // may not list the new ACTION rows until Identity Hub is re-saved.
  const canViewSlackNotifications =
    canMarketplaceAdmin ||
    useIamPermission(perms.MARKETPLACE_ADMIN_GET_NOTIFICATION_SETTINGS);
  const canEditSlackNotifications =
    canMarketplaceAdmin ||
    useIamPermission(perms.MARKETPLACE_ADMIN_UPDATE_NOTIFICATION_SETTINGS);
  const [mode, setMode] = useState<PageMode>({ kind: "list", tab: "vendors" });
  const { data: pendingRows = [] } = useQuery({
    ...adminPendingVendorsQueryOptions(),
    enabled: canMarketplaceAdmin,
  });
  const { data: pendingHandoffs = [] } = useQuery({
    ...adminPendingHandoffsQueryOptions(),
    enabled: canMarketplaceAdmin,
  });

  if (mode.kind === "form") {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 sm:p-6">
        <header className="flex flex-col gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-7 w-fit px-2 text-muted-foreground hover:text-foreground"
            onClick={() => setMode({ kind: "list", tab: mode.pendingContext ? "pending" : "vendors" })}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {mode.pendingContext
              ? t("marketplace.admin.backToPending")
              : t("marketplace.admin.backToVendors", { defaultValue: "All partners" })}
          </Button>
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {mode.editingVendor
                ? t("marketplace.admin.editVendor")
                : t("marketplace.admin.addVendor")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {mode.pendingContext
                ? t("marketplace.admin.onboardPendingSubtitle", {
                    label: mode.pendingContext.rawLabel,
                    client: mode.pendingContext.tenantName,
                  })
                : t("marketplace.admin.formSubtitle")}
            </p>
          </div>
        </header>

        <AdminVendorForm
          key={mode.editingVendor?.id ?? mode.pendingContext?.id ?? "new"}
          vendor={mode.editingVendor}
          pendingContext={mode.pendingContext}
          onClose={() => setMode({ kind: "list", tab: mode.pendingContext ? "pending" : "vendors" })}
        />
      </div>
    );
  }

  const activeTab = mode.tab;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 sm:p-6">
      <header className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("marketplace.admin.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("marketplace.admin.subtitle")}
          </p>
        </div>
        {activeTab === "vendors" && perms.MARKETPLACE_ADMIN_CREATE_VENDOR.isOn ? (
          <Button onClick={() => setMode({ kind: "form" })}>
            <Plus className="mr-2 h-4 w-4" />
            {t("marketplace.admin.addVendor")}
          </Button>
        ) : null}
      </header>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setMode({ kind: "list", tab: value as AdminTab })}
      >
        <TabsList variant="line" className="w-full justify-start">
          <TabsTrigger value="vendors">
            {t("marketplace.admin.tabs.vendors")}
          </TabsTrigger>
          <TabsTrigger value="pending" className="inline-flex items-center gap-2">
            {t("marketplace.admin.tabs.pending")}
            {pendingRows.length > 0 ? (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-status-warning-subtle px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-status-warning">
                {pendingRows.length}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="handoffs" className="inline-flex items-center gap-2">
            {t("marketplace.admin.tabs.handoffs", "Pending emails")}
            {pendingHandoffs.length > 0 ? (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-status-warning-subtle px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-status-warning">
                {pendingHandoffs.length}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="team-recipients">
            {t("marketplace.admin.tabs.teamRecipients")}
          </TabsTrigger>
          <TabsTrigger value="slack-notifications">
            {t("marketplace.admin.tabs.slackNotifications")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vendors" className="mt-6">
          <AdminVendorsList
            canList={perms.MARKETPLACE_ADMIN_LIST_VENDORS.isOn}
            canEdit={perms.MARKETPLACE_ADMIN_REPLACE_VENDOR.isOn}
            onEdit={(vendor) => setMode({ kind: "form", editingVendor: vendor })}
          />
        </TabsContent>

        <TabsContent value="pending" className="mt-6">
          <AdminPendingVendorsList
            canList={canMarketplaceAdmin}
            canLink={canMarketplaceAdmin}
            canCreateVendor={perms.MARKETPLACE_ADMIN_CREATE_VENDOR.isOn}
            canRemove={canMarketplaceAdmin}
            onOnboard={(row) =>
              setMode({ kind: "form", pendingContext: toPendingContext(row) })
            }
          />
        </TabsContent>

        <TabsContent value="handoffs" className="mt-6">
          <AdminPendingHandoffsList
            canList={canMarketplaceAdmin}
            canRetry={perms.MARKETPLACE_ADMIN_RETRY_ORDER_HANDOFF.isOn}
          />
        </TabsContent>

        <TabsContent value="team-recipients" className="mt-6">
          <AdminTeamRecipients
            canList={perms.MARKETPLACE_ADMIN_LIST_TEAM_RECIPIENTS.isOn}
            canCreate={perms.MARKETPLACE_ADMIN_CREATE_TEAM_RECIPIENT.isOn}
            canUpdate={perms.MARKETPLACE_ADMIN_UPDATE_TEAM_RECIPIENT.isOn}
            canDelete={perms.MARKETPLACE_ADMIN_DELETE_TEAM_RECIPIENT.isOn}
          />
        </TabsContent>

        <TabsContent value="slack-notifications" className="mt-6">
          <AdminSlackNotifications
            canView={canViewSlackNotifications}
            canEdit={canEditSlackNotifications}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
